// ══════════════════════════════════════════════════════════
// CUSTOM APP REQUESTS SERVICE
//
// Solicitudes de app personalizada para radios.
// Consume endpoints del backend PHP.
//
// FRONTEND NO PUEDE:
// - Crear builds, repositorios, certificados.
// - Marcar PUBLISHED, APPROVED ni IN_PRODUCTION.
// - Solicitar sin entitlement custom_app.
// - Usar assets de otra radio.
// - Enviar credenciales, passwords, API keys.
// ══════════════════════════════════════════════════════════

import { backendApiRequest } from '@/services/rfwBackend';
import { getSupabaseClient } from '@/services/supabase/client';
import type { BackendResponse } from '@/types/rfwBackend';
import type {
  CustomAppRequestPayload,
  CustomAppRequestResponse,
  CustomAppPlatform,
  CustomAppRequestStatus,
} from '@/types/customApps';
import {
  validateCustomAppPayload,
  ALL_CUSTOM_APP_PLATFORMS,
} from '@/types/customApps';
import { adminEntitlementsData } from '@/mocks/adminEntitlements';

function supabase() {
  return getSupabaseClient();
}

// ═══════════════ BACKEND API (PRIMARY) ═══════════════

/**
 * Crear una solicitud de app personalizada.
 * POST /api/v1/radios/{radioId}/custom-app-requests
 *
 * Validaciones:
 * - Requiere entitlement custom_app.
 * - Valida que no se envíen campos prohibidos.
 * - Valida que las plataformas sean válidas.
 * - Valida que los assets pertenezcan a la radio (backend también valida).
 */
export async function createCustomAppRequest(
  radioId: string,
  payload: CustomAppRequestPayload,
): Promise<CustomAppRequestResponse> {
  // 1. Validar campos prohibidos
  const prohibitedViolations = validateCustomAppPayload(payload as unknown as Record<string, unknown>);
  if (prohibitedViolations.length > 0) {
    throw new Error(prohibitedViolations.join(' '));
  }

  // 2. Validar plataformas
  const invalidPlatforms = payload.platforms.filter((p) => !ALL_CUSTOM_APP_PLATFORMS.includes(p));
  if (invalidPlatforms.length > 0) {
    throw new Error(`Plataformas inválidas: ${invalidPlatforms.join(', ')}`);
  }

  if (payload.platforms.length === 0) {
    throw new Error('Debe seleccionar al menos una plataforma.');
  }

  // 3. Validar que los assets pertenezcan a la radio (si se enviaron)
  if (payload.logo_asset_id || payload.icon_asset_id || payload.splash_asset_id) {
    await validateAssetOwnership(radioId, [
      payload.logo_asset_id,
      payload.icon_asset_id,
      payload.splash_asset_id,
    ].filter(Boolean) as string[]);
  }

  // 4. Enviar al backend
  try {
    const response = await backendApiRequest<CustomAppRequestResponse>(
      `/api/v1/radios/${radioId}/custom-app-requests`,
      { method: 'POST', body: payload as unknown as Record<string, unknown> },
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.message || 'Error al crear la solicitud de app.');
  } catch (err) {
    // Fallback: si el backend no está disponible, no podemos crear la solicitud
    if (err instanceof Error && err.message.includes('No se pudo conectar')) {
      throw new Error(
        'El backend no está disponible. La solicitud de app personalizada requiere conexión con el servidor.',
      );
    }
    throw err;
  }
}

/**
 * Listar solicitudes de app personalizada de una radio.
 * GET /api/v1/radios/{radioId}/custom-app-requests
 */
export async function getCustomAppRequests(
  radioId: string,
): Promise<CustomAppRequestResponse[]> {
  try {
    const response = await backendApiRequest<CustomAppRequestResponse[]>(
      `/api/v1/radios/${radioId}/custom-app-requests`,
    );

    if (response.success && Array.isArray(response.data)) {
      return response.data;
    }

    return [];
  } catch {
    // Fallback: devolver solicitudes mock
    return adminEntitlementsData.customAppRequests as CustomAppRequestResponse[];
  }
}

// ═══════════════ ASSET VALIDATION ═══════════════

/**
 * Validar que los assets pertenezcan a la radio especificada.
 * Verifica que media_assets.radio_id === radioId.
 */
async function validateAssetOwnership(radioId: string, assetIds: string[]): Promise<void> {
  if (assetIds.length === 0) return;

  try {
    const { data, error } = await supabase()
      .from('media_assets')
      .select('id, radio_id')
      .in('id', assetIds);

    if (error) {
      throw new Error('Error al validar assets: ' + error.message);
    }

    if (!data || data.length !== assetIds.length) {
      const found = new Set(data?.map((a) => a.id) || []);
      const missing = assetIds.filter((id) => !found.has(id));
      throw new Error(`Assets no encontrados: ${missing.join(', ')}`);
    }

    const foreignAssets = data.filter((a) => a.radio_id !== radioId);
    if (foreignAssets.length > 0) {
      throw new Error(
        `Los siguientes assets no pertenecen a esta radio: ${foreignAssets.map((a) => a.id).join(', ')}`,
      );
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Assets no encontrados')) {
      throw err;
    }
    if (err instanceof Error && err.message.startsWith('Los siguientes assets')) {
      throw err;
    }
    // Si hay error de conexión, permitimos que el backend haga su propia validación
    console.warn('[customAppRequests] No se pudo validar asset ownership, el backend lo hará:', err);
  }
}

// ═══════════════ HELPERS ═══════════════

/**
 * Determinar si se puede crear una nueva solicitud basado en el estado actual.
 */
export function canCreateNewRequest(requests: CustomAppRequestResponse[]): {
  allowed: boolean;
  reason: string | null;
} {
  if (requests.length === 0) {
    return { allowed: true, reason: null };
  }

  const blockingStatuses: CustomAppRequestStatus[] = ['PENDING', 'IN_REVIEW', 'APPROVED', 'IN_PRODUCTION'];
  const hasBlocking = requests.some((r) => blockingStatuses.includes(r.status));

  if (hasBlocking) {
    const blocking = requests.find((r) => blockingStatuses.includes(r.status));
    return {
      allowed: false,
      reason: `Ya existe una solicitud en estado "${blocking?.status}". Esperá a que se complete antes de crear una nueva.`,
    };
  }

  return { allowed: true, reason: null };
}